import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// charts
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale
);

// @mui/material components
import { makeStyles } from '@mui/styles';

// @mui/icons-material
import DateRange from '@mui/icons-material/DateRange';
import Store from '@mui/icons-material/Store';

import Company from '@mui/icons-material/Business';
import Workstation from '@mui/icons-material/Computer';
import Equalizer from '@mui/icons-material/Equalizer';
import RefreshIcon from '@mui/icons-material/Refresh';
import Environment from '@mui/icons-material/SettingsInputComponent';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import { selectSessionStatus } from '../../features/session/sessionSlice';

import { primaryColor } from '@themes/defaultThemeProps';
import {
  useGetClientsQuery,
  useGetClientStatsQuery,
  useLazyGetClientStatsQuery,
} from 'api/client.api';
import styles from 'assets/jss/material-dashboard-pro-react/views/dashboardStyle.js';
// import { themedCardHeader } from "@themes/defaultThemesProps"
const useStyles = makeStyles(styles);

export default function Dashboard() {
  const sessionStatus = useSelector(selectSessionStatus);
  const { data: clients } = useGetClientsQuery();
  const { data: clientStats, isFetching, isSuccess } = useGetClientStatsQuery();
  let statsStatus = 'failure';
  if (isFetching) {
    statsStatus = 'pending';
  } else if (isSuccess) {
    statsStatus = 'success';
  }
  const [triggerGetClientStats] = useLazyGetClientStatsQuery();

  const clientCount = clientStats ? clientStats.clientCount : 0;
  const environmentCount = clientStats ? clientStats.tenantCount : 0;
  const locationCount = clientStats ? clientStats.locationCount : 0;
  const workstationCount = clientStats ? clientStats.workstationCount : 0;

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && sessionStatus === 'active') {
      setInitialized(true);
      setRefresh(true);
    }
  }, [initialized, sessionStatus]);

  const [refresh, setRefresh] = useState(false);
  const [refreshAvailable, setRefreshAvailable] = useState(false);

  const refreshStats = () => {
    if (initialized && sessionStatus === 'active') setRefresh(true);
  };

  let timer = useRef();

  useEffect(() => {
    if (refresh) {
      triggerGetClientStats().unwrap();
      setRefresh(false);
      setRefreshAvailable(false);
      timer.current = setTimeout(() => {
        setRefreshAvailable(true);
      }, 10000);
    }
  }, [refresh]);

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (initialized && statsStatus === 'success') {
      let labels = [];
      let series = [];
      clients.forEach((client) => {
        var count = 0;
        const statClient = clientStats.sessions.find(
          (c) => c._id === client._id
        );
        if (statClient) count = statClient.count;
        if (count > 0) {
          labels.push(client.name);
          series.push(count);
        }
      });

      setChartData({
        ...(chartData || {}),
        labels: labels,
        datasets: [
          {
            data: series,
            borderWidth: 1,
          },
        ],
      });
    }
  }, [clientStats, initialized, statsStatus]);

  const classes = useStyles();
  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Button
            color='info'
            onClick={refreshStats}
            className={classes.refreshButton}
            startIcon={<RefreshIcon />}
            disabled={!refreshAvailable}
          >
            Refresh
          </Button>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='primary' stats icon>
              <CardIcon color='primary'>
                <Company />
              </CardIcon>
              <p className={classes.cardCategory}>Companies</p>
              <h3 className={classes.cardTitle}>{clientCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                Currently Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='danger' stats icon>
              <CardIcon color='danger'>
                <Environment />
              </CardIcon>
              <p className={classes.cardCategory}>Environments</p>
              <h3 className={classes.cardTitle}>{environmentCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                Active Month-to-Date, Production and Test
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='info' stats icon>
              <CardIcon color='info'>
                <Store />
              </CardIcon>
              <p className={classes.cardCategory}>Locations</p>
              <h3 className={classes.cardTitle}>{locationCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                Currently Active, Production and Test
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='warning' stats icon>
              <CardIcon color='warning'>
                <Workstation />
              </CardIcon>
              <p className={classes.cardCategory}>Workstations</p>
              <h3 className={classes.cardTitle}>{workstationCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                Active Month-to-Date, Production and Test
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color='primary' icon>
              <CardIcon color='primary'>
                <Equalizer />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Activity by Company</h4>
            </CardHeader>
            <CardBody>
              <div style={{ height: '400px' }}>
                <Bar
                  data={chartData}
                  options={{
                    indexAxis: 'y',
                    backgroundColor: primaryColor[0],
                    maintainAspectRatio: false,
                    interaction: {
                      intersect: false,
                      axis: 'y',
                      mode: 'index',
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </div>
            </CardBody>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                Activity for the last 30 days
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
